"use client";
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { Input } from '@/components/ui/input';
import {
  buildCatalogHierarchy,
  buildCatalogPath,
  filterProductsByCatalog,
  formatProductCategory,
  slugifyRouteSegment,
} from '@/lib/productRoutes';
import { productPropType } from '@/lib/propTypes';

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.08,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: 'easeOut' },
  },
};

const getCatalogHeading = (department, subcategory) => {
  if (department && subcategory) {
    return `${subcategory.name} for ${department.name}`;
  }

  if (department) {
    return `${department.name} Department`;
  }

  return 'Browse by Department';
};

const getCatalogDescription = (department, subcategory) => {
  if (department && subcategory) {
    return `Explore every ${subcategory.name.toLowerCase()} style currently available in the ${department.name.toLowerCase()} department.`;
  }

  if (department) {
    if (!department.subcategories?.length) {
      return `Browse every available pair currently listed in the ${department.name.toLowerCase()} department.`;
    }

    return `Choose a subdivision inside ${department.name} and narrow down to the exact pair you want.`;
  }

  return 'Start with a department, move into a subdivision, and then open the exact product page you want.';
};

export default function CatalogBrowser({
  products = [],
  selectedDepartmentSlug = '',
  selectedSubcategorySlug = '',
}) {
  const searchParams = useSearchParams();
  const [search, setSearch] = useState('');

  const catalogProducts = useMemo(() => filterProductsByCatalog(products, {
    departmentSlug: selectedDepartmentSlug,
    subcategorySlug: selectedSubcategorySlug,
  }), [products, selectedDepartmentSlug, selectedSubcategorySlug]);

  const hierarchy = useMemo(() => buildCatalogHierarchy(products), [products]);
  const activeDepartment = useMemo(
    () => hierarchy.find((department) => department.slug === slugifyRouteSegment(selectedDepartmentSlug)) || null,
    [hierarchy, selectedDepartmentSlug]
  );
  const activeSubcategory = useMemo(
    () =>
      activeDepartment?.subcategories.find(
        (subcategory) => subcategory.slug === slugifyRouteSegment(selectedSubcategorySlug)
      ) || null,
    [activeDepartment, selectedSubcategorySlug]
  );

  const visibleProducts = useMemo(() => {
    const searchQuery = search.trim().toLowerCase();
    if (!searchQuery) return catalogProducts;

    return catalogProducts.filter((product) =>
      [product.name, product.description, product.category, product.subcategory, formatProductCategory(product)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchQuery))
    );
  }, [catalogProducts, search]);

  useEffect(() => {
    setSearch('');
  }, [selectedDepartmentSlug, selectedSubcategorySlug]);

  useEffect(() => {
    if (searchParams?.get('focus') !== 'search') return;

    const searchInput = document.getElementById('products-search');
    searchInput?.focus();
    searchInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [searchParams]);

  return (
    <div className="space-y-8">
      <div className="rounded-[2.3rem] border border-[#eadfc9] bg-white/90 p-5 shadow-[0_18px_50px_rgba(26,48,35,0.06)] sm:p-7">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-[#667063]">
          <Link href="/" className="transition hover:text-[#24552d]">Home</Link>
          <span>/</span>
          <Link href="/products" className="transition hover:text-[#24552d]">Products</Link>
          {activeDepartment ? (
            <>
              <span>/</span>
              <Link href={buildCatalogPath({ department: activeDepartment.name })} className="transition hover:text-[#24552d]">
                {activeDepartment.name}
              </Link>
            </>
          ) : null}
          {activeSubcategory ? (
            <>
              <span>/</span>
              <span className="font-semibold text-[#223326]">{activeSubcategory.name}</span>
            </>
          ) : null}
        </nav>

        <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#2b5f34]">Department Catalog</p>
            <h1 className="mt-3 text-3xl font-playfair font-bold text-[#162218] sm:text-4xl">
              {getCatalogHeading(activeDepartment, activeSubcategory)}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-[#62675f] sm:text-base">
              {getCatalogDescription(activeDepartment, activeSubcategory)}
            </p>
          </div>

          <div className="rounded-full bg-[#f6f1e4] px-4 py-2 text-sm font-semibold text-[#35553a]">
            {visibleProducts.length} product{visibleProducts.length === 1 ? '' : 's'}
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <div className="flex flex-wrap gap-3">
            <Link
              href="/products"
              className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
                !activeDepartment
                  ? 'bg-[#173822] text-white shadow-[0_12px_24px_rgba(23,56,34,0.16)]'
                  : 'border border-[#e6dcc7] bg-white text-[#566258] hover:border-[#cdb98f] hover:bg-[#fffaf0]'
              }`}
            >
              All Departments
            </Link>
            {hierarchy.map((department) => (
              <Link
                key={department.slug}
                href={buildCatalogPath({ department: department.name })}
                className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
                  activeDepartment?.slug === department.slug
                    ? 'bg-[#173822] text-white shadow-[0_12px_24px_rgba(23,56,34,0.16)]'
                    : 'border border-[#e6dcc7] bg-white text-[#566258] hover:border-[#cdb98f] hover:bg-[#fffaf0]'
                }`}
              >
                {department.name}
              </Link>
            ))}
          </div>

          {activeDepartment ? (
            <div className="flex flex-wrap gap-3">
              <Link
                href={buildCatalogPath({ department: activeDepartment.name })}
                className={`rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                  !activeSubcategory
                    ? 'bg-[#24552d] text-white'
                    : 'border border-[#e6dcc7] bg-[#fffdfa] text-[#566258] hover:border-[#cdb98f]'
                }`}
              >
                All {activeDepartment.name}
              </Link>
              {activeDepartment.subcategories.map((subcategory) => (
                <Link
                  key={subcategory.slug}
                  href={buildCatalogPath({
                    department: activeDepartment.name,
                    subcategory: subcategory.name,
                  })}
                  className={`rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                    activeSubcategory?.slug === subcategory.slug
                      ? 'bg-[#24552d] text-white'
                      : 'border border-[#e6dcc7] bg-[#fffdfa] text-[#566258] hover:border-[#cdb98f]'
                  }`}
                >
                  {subcategory.name}
                </Link>
              ))}
            </div>
          ) : null}

          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8d9386]" />
            <Input
              id="products-search"
              placeholder="Search within this section..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-13 rounded-full border-[#e7dcc7] bg-white pl-12 pr-4 text-[15px] shadow-none focus:border-[#cbb27f]"
            />
          </div>
        </div>
      </div>

      <motion.div
        variants={gridVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4"
      >
        {visibleProducts.map((product) => (
          <motion.div key={product.id} variants={cardVariants}>
            <ProductCard product={product} />
          </motion.div>
        ))}
      </motion.div>

      {visibleProducts.length === 0 ? (
        <div className="rounded-[2rem] border border-dashed border-[#d9ccb0] bg-[#fffaf1] px-6 py-12 text-center">
          <p className="text-lg font-semibold text-[#243626]">No products found in this section.</p>
          <p className="mt-2 text-sm text-[#6a7067]">Try another search or move to a different subdivision.</p>
        </div>
      ) : null}
    </div>
  );
}

CatalogBrowser.propTypes = {
  products: PropTypes.arrayOf(productPropType),
  selectedDepartmentSlug: PropTypes.string,
  selectedSubcategorySlug: PropTypes.string,
};
